export const FOR_WHO_OPTIONS = [
  {
    id: 'self',
    label: 'For my own business',
    description: "I'm finding leads for a company I run or am building.",
  },
  {
    id: 'client',
    label: 'For a client',
    description: "I run a lead-gen service and I'm setting this up for someone I work for.",
  },
];

export const BUSINESS_TYPES = [
  {
    id: 'agency',
    label: 'Marketing / creative agency',
    description: 'Branding, content, performance, design.',
    industry: 'Marketing Agency',
    sizeLabel: '10-80 employees',
    sizeRanges: ['11-50', '51-200'],
    roleFocus: 'marketing',
    defaultProblem: 'creative teams under pressure to deliver more client work',
  },
  {
    id: 'saas',
    label: 'B2B SaaS',
    description: 'Software sold as a subscription.',
    industry: 'SaaS',
    sizeLabel: '20-200 employees',
    sizeRanges: ['11-50', '51-200'],
    roleFocus: 'product',
    defaultProblem: 'teams evaluating new software to improve growth or operations',
  },
  {
    id: 'consult',
    label: 'Consulting / coaching',
    description: 'Expertise, advice, training, and transformation work.',
    industry: 'Professional Services',
    sizeLabel: '1-50 employees',
    sizeRanges: ['1-10', '11-50'],
    roleFocus: 'operations',
    defaultProblem: 'leaders looking for outside expertise to move faster',
  },
  {
    id: 'service',
    label: 'Local service business',
    description: 'Bookkeeping, legal, real estate, trades.',
    industry: 'Professional Services',
    sizeLabel: '1-20 employees',
    sizeRanges: ['1-10', '11-50'],
    roleFocus: 'operations',
    defaultProblem: 'local operators with manual follow-up and admin work',
  },
  {
    id: 'ecom',
    label: 'E-commerce / DTC',
    description: 'Physical products sold online.',
    industry: 'E-commerce',
    sizeLabel: '5-100 employees',
    sizeRanges: ['1-10', '11-50', '51-200'],
    roleFocus: 'marketing',
    defaultProblem: 'commerce teams trying to improve repeatable acquisition',
  },
  {
    id: 'other',
    label: 'Something else',
    description: "I'll tell you in my own words below.",
    industry: 'Custom',
    sizeLabel: 'Custom',
    sizeRanges: [],
    roleFocus: 'marketing',
    defaultProblem: 'prospects who match the details you describe',
  },
];

export const GOALS = [
  {
    id: 'cold-outreach',
    label: 'Send cold outreach',
    description: 'Find decision makers I can email or message.',
    searchType: 'people',
    entityLabel: 'People',
    roles: 'Director, VP, Head of Marketing',
    roleFilter: 'marketing',
    seniorityFilter: 'Director',
    signals: 'Active on LinkedIn, recent hiring, visible growth signals',
    headlineTemplate: ({ business }) => `${businessAudience(business)} marketing leaders in the Nordics`,
    aiColumns: [
      {
        label: 'Pain points',
        prompt: 'Infer 2-3 concrete pains this person likely owns. Use their title, company industry, size, location, and visible signals. Avoid generic claims and tie each pain to a specific signal.',
      },
      {
        label: 'Cold email draft',
        prompt: 'Write a 70-90 word first-touch email with a specific opening line, one relevant business problem, one crisp value hypothesis, and a soft CTA. Keep it human and avoid buzzwords.',
      },
      {
        label: 'Fit score',
        prompt: 'Score fit from 1-10 using role relevance, company fit, geography, and available intent signals. Include the score plus a one-sentence reason grounded in the row data.',
      },
    ],
  },
  {
    id: 'partner',
    label: 'Find partner companies',
    description: 'Companies I could integrate, co-market, or refer with.',
    searchType: 'companies',
    entityLabel: 'Companies',
    roles: 'BD, Partnerships, Founder',
    roleFilter: null,
    seniorityFilter: null,
    signals: 'Complementary product, same ICP, active hiring',
    headlineTemplate: ({ business }) => `Complementary ${businessIndustry(business)} companies serving the same ICP`,
    aiColumns: [
      {
        label: 'Partnership angle',
        prompt: 'Identify the strongest partnership motion: co-marketing, integration, referral, or channel. Explain why this company is complementary and name the first outreach hook.',
      },
      {
        label: 'Shared ICP',
        prompt: 'Describe the overlap between this company and our ICP using industry, customer segment, size, geography, and signals. Be specific about who both sides likely sell to.',
      },
      {
        label: 'Fit score',
        prompt: 'Score partnership fit from 1-10 using ICP overlap, complementary offer, market credibility, and likely ease of starting a conversation. Include the score and one reason.',
      },
    ],
  },
  {
    id: 'event',
    label: 'Promote an event / launch',
    description: 'People who would attend a webinar, demo, or launch.',
    searchType: 'people',
    entityLabel: 'People',
    roles: 'Practitioner, Manager, Marketing Lead',
    roleFilter: 'marketing',
    seniorityFilter: 'Manager',
    signals: 'Engages with topic, recently posted, conference or launch interest',
    headlineTemplate: ({ business }) => `${businessAudience(business)} practitioners active around launch-relevant topics`,
    aiColumns: [
      {
        label: 'Invite angle',
        prompt: 'Write a one-sentence invite angle that connects this person\'s role, company context, and visible signals to the event or launch topic.',
      },
      {
        label: 'Opening line',
        prompt: 'Draft a short personalized opener for an event invite. Mention one concrete signal from the row and make the transition to the invitation feel natural.',
      },
      {
        label: 'Fit score',
        prompt: 'Score invite relevance from 1-10 using role fit, topic fit, geography, and engagement signals. Include the score and one sentence explaining the strongest evidence.',
      },
    ],
  },
  {
    id: 'investor',
    label: 'Find buyers / investors',
    description: 'Acquirers, VCs, or strategic partners.',
    searchType: 'people',
    entityLabel: 'People',
    roles: 'Partner, Principal, Investor',
    roleFilter: 'Investor',
    seniorityFilter: 'VP',
    signals: 'Recent investments, thesis match, strategic category interest',
    headlineTemplate: () => 'Investors and strategic buyers focused on Nordic B2B tooling',
    aiColumns: [
      {
        label: 'Investment thesis',
        prompt: 'Infer the relevant investment, acquisition, or strategic thesis this contact may care about. Ground it in role, firm/company context, category, geography, and recent signals.',
      },
      {
        label: 'Warm intro angle',
        prompt: 'Draft a warm intro angle that names the shared category context, why this contact is relevant, and the smallest useful next ask. Keep it short and relationship-safe.',
      },
      {
        label: 'Fit score',
        prompt: 'Score investor or strategic buyer fit from 1-10 using thesis match, role seniority, category relevance, and signal strength. Include the score and one evidence-backed reason.',
      },
    ],
  },
];

export const DEFAULT_GEOGRAPHY = ['Norway', 'Denmark', 'Sweden'];

function businessIndustry(business) {
  if (!business || business.id === 'other') return 'B2B';
  return business.industry.toLowerCase();
}

function businessAudience(business) {
  if (!business || business.id === 'other') return 'Best-fit';
  return business.industry;
}
